'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SortControlsProps {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  resultCount: number;
}

const SortControls = ({
  sortBy,
  sortOrder,
  onSortChange,
  viewMode,
  onViewModeChange,
  resultCount
}: SortControlsProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const sortOptions = [
    { value: 'relevance', label: 'Relevance', icon: 'StarIcon' },
    { value: 'price', label: 'Price', icon: 'CurrencyDollarIcon' },
    { value: 'date', label: 'Date Listed', icon: 'CalendarIcon' },
    { value: 'year', label: 'Year', icon: 'ClockIcon' },
    { value: 'alphabetical', label: 'Name', icon: 'Bars3BottomLeftIcon' }
  ];

  const currentSort = sortOptions.find(option => option.value === sortBy) || sortOptions[0];

  const handleSortSelect = (value: string) => {
    if (value === sortBy) {
      onSortChange(value, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(value, 'asc');
    }
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card border border-border rounded-industrial p-4 industrial-shadow">
      {/* Results Count */}
      <div className="flex items-center space-x-2">
        <Icon name="FunnelIcon" size={16} className="text-text-secondary" />
        <span className="text-sm text-text-secondary">
          Showing <span className="font-bold text-foreground">{resultCount.toLocaleString()}</span> equipment listings
        </span>
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-4">
        {/* Sort Dropdown */}
        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center space-x-2 px-3 py-2 border border-border rounded-industrial hover:bg-muted transition-industrial"
          >
            <Icon name={currentSort.icon as any} size={16} className="text-text-secondary" />
            <span className="text-sm font-heading uppercase tracking-wide">
              Sort: {currentSort.label}
            </span>
            <Icon 
              name={sortOrder === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
              size={16} 
              className="text-text-secondary" 
            />
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-industrial industrial-shadow-lg z-10">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSortSelect(option.value)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-sm hover:bg-muted transition-industrial ${
                    option.value === sortBy ? 'bg-muted text-primary' : 'text-popover-foreground'
                  }`}
                >
                  <Icon name={option.icon as any} size={16} />
                  <span className="font-heading uppercase tracking-wide">{option.label}</span>
                  {option.value === sortBy && (
                    <Icon 
                      name={sortOrder === 'asc' ? 'ArrowUpIcon' : 'ArrowDownIcon'} 
                      size={14} 
                      className="ml-auto" 
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center border border-border rounded-industrial overflow-hidden">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`p-2 transition-industrial ${
              viewMode === 'grid' ?'bg-primary text-primary-foreground' :'bg-background text-text-secondary hover:bg-muted'
            }`}
            title="Grid View"
          >
            <Icon name="Squares2X2Icon" size={16} />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`p-2 transition-industrial ${
              viewMode === 'list' ?'bg-primary text-primary-foreground' :'bg-background text-text-secondary hover:bg-muted'
            }`}
            title="List View"
          >
            <Icon name="ListBulletIcon" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SortControls;